import { UMAP } from 'umap-js';
import { Matrix, SVD } from 'ml-matrix';

// Number of PCA components to keep before running UMAP
const PCA_COMPONENTS = 20;

/**
 * Reduce embeddings using PCA
 * @param {Array<Array<number>>} embeddings - Original embedding vectors
 * @param {number} nComponents - Number of principal components to keep
 * @returns {Object} Reduced data, PCA components and mean vector
 */
export const applyPCA = (embeddings, nComponents = PCA_COMPONENTS) => {
  const matrix = new Matrix(embeddings);
  const mean = matrix.mean('column');
  matrix.subRowVector(mean);
  const svd = new SVD(matrix, { autoTranspose: true });
  const compMatrix = svd.rightSingularVectors.subMatrix(
    0,
    svd.rightSingularVectors.rows - 1,
    0,
    nComponents - 1
  );
  const reduced = matrix.mmul(compMatrix);
  return {
    data: reduced.to2DArray(),
    components: compMatrix.to2DArray(),
    mean
  };
};

/**
 * Perform UMAP dimensionality reduction on embeddings
 * @param {Array<Array<number>>} embeddings - Array of embedding vectors
 * @param {number} dimensions - Target dimensions (default: 2)
 * @returns {Object} UMAP instance and reduced data
 */
export const performUMAP = (embeddings, dimensions = 2) => {
  try {
    // SAFETY CHECK: Use a simple projection instead of UMAP to avoid stack overflow
    if (embeddings[0].length > 2000) {
      console.log("Using simple projection to avoid UMAP stack overflow");
      return performSimpleProjection(embeddings, dimensions);
    }

    // Apply PCA before UMAP to speed up processing
    const { data: pcaData, components, mean } = applyPCA(embeddings, PCA_COMPONENTS);

    // For smaller vectors, try UMAP with safe parameters
    const umap = new UMAP({
      nComponents: dimensions,
      nNeighbors: 5,       // Lower for smaller datasets
      minDist: 0.1,        // Controls how tightly points are packed
      spread: 1.0,
      nEpochs: 100,        // Reduced for faster processing
      nTrees: 5            // Reduced for less stack usage
    });

    // Fit and transform the PCA reduced embeddings
    const reducedData = umap.fit(pcaData);

    return {
      umap,       // Return the fitted UMAP model for future transforms
      // pca: { components, mean },
      reducedData // Reduced dimensional data
    };
  } catch (error) {
    console.error("UMAP error:", error);
    // Fallback to simple projection if UMAP fails
    return performSimpleProjection(embeddings, dimensions);
  }
};

/**
 * Simple projection as a fallback when UMAP fails
 * Uses first two principal components as a simple alternative
 */
const performSimpleProjection = (embeddings, dimensions = 2) => {
  // Get first two dimensions from each embedding for simplicity
  // This is just a simplistic approach to avoid the recursion issue
  const reducedData = embeddings.map(vector => {
    // Just take first two dimensions and scale them to reasonable values
    // This is NOT real PCA, just a simple projection to see something on screen
    const x = vector.slice(0, 100).reduce((sum, val, i) => sum + val * (i % 5), 0) / 25;
    const y = vector.slice(0, 100).reduce((sum, val, i) => sum + val * ((i+2) % 7), 0) / 25;
    
    return [x, y];
  });

  // Create a dummy UMAP object that just returns the projected data
  const dummyUmap = {
    transform: (data) => {
      return data.map(vector => {
        const x = vector.slice(0, 100).reduce((sum, val, i) => sum + val * (i % 5), 0) / 25;
        const y = vector.slice(0, 100).reduce((sum, val, i) => sum + val * ((i+2) % 7), 0) / 25;
        return [x, y];
      });
    }
  };

  return {
    umap: dummyUmap,
    reducedData,
    pca: null
  };
};

/**
 * Transform new embedding using an existing UMAP model
 * @param {Object} umap - Fitted UMAP model
 * @param {Array<number>} embedding - Single embedding vector to transform
 * @returns {Array<number>} Reduced dimensional data for the embedding
 */
export const transformWithUMAP = (model, embedding) => {
  try {
    let vector = embedding;

    if (model && model.pca) {
      const row = Matrix.rowVector(vector).subRowVector(model.pca.mean);
      const compMatrix = new Matrix(model.pca.components);
      vector = row.mmul(compMatrix).to1DArray();
    }

    const umap = model.umap || model;
    return umap.transform([vector])[0];
  } catch (error) {
    console.error("Transform error:", error);
    // Fallback to simple projection if transform fails
    const x = embedding.slice(0, 100).reduce((sum, val, i) => sum + val * (i % 5), 0) / 25;
    const y = embedding.slice(0, 100).reduce((sum, val, i) => sum + val * ((i+2) % 7), 0) / 25;
    return [x, y];
  }
}; 